import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import MoreVertIcon from '@mui/icons-material/MoreVertRounded'
import { useCallback, useMemo, useState, useRef, MouseEvent, memo } from 'react'
import {
  ClickAwayListener,
  Divider,
  ListItemButton,
  Paper,
  Popper,
  IconButton,
} from '@mui/material'
import { intl } from '../../../../../i18n'

type TemplateActionMenuProps = {
  onUpdate: () => void
}

const StyledMoreVertIcon = styled(MoreVertIcon)({
  color: colorPalette.monotone[4],
  height: '16px',
  width: '16px',
})

const StyledIconButton = styled(IconButton)({
  padding: '4px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[2],
  },
})

const MenuItemListWrapper = styled('div')({
  backgroundColor: colorPalette.monotone[0],
  display: 'flex',
  flexDirection: 'column',
  padding: '4px',
  borderRadius: '4px',
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  width: '230px',
})

const MenuItemListHeader = styled('div')({
  color: colorPalette.monotone[5],
  fontSize: '12px',
  fontWeight: 600,
  padding: '8px',
})

const MenuItemListDivider = styled(Divider)({
  borderColor: colorPalette.monotone[2],
})

const MenuItemListArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px',
  gap: '4px',
})

const MenuItem = styled(ListItemButton)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  color: colorPalette.monotone[4],
  fontSize: '12px',
})

const MenuItemList = memo(({ onUpdate }: TemplateActionMenuProps) => {
  return (
    <MenuItemListWrapper>
      <MenuItemListHeader>
        {intl.formatMessage({ id: 'menu' })}
      </MenuItemListHeader>
      <MenuItemListDivider />
      <MenuItemListArea>
        <MenuItem onClick={onUpdate}>テンプレートを編集する</MenuItem>
      </MenuItemListArea>
    </MenuItemListWrapper>
  )
})

MenuItemList.displayName = 'MenuItemList'

export const TemplateActionMenu = ({ onUpdate }: TemplateActionMenuProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const active = useMemo(() => Boolean(anchorEl), [anchorEl])

  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      if (!active) {
        setAnchorEl(buttonRef.current)
      } else {
        setAnchorEl(null)
      }
    },
    [active]
  )

  const handleUpdate = useCallback(() => {
    setAnchorEl(null)
    onUpdate()
  }, [onUpdate])

  const handleClickAway = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <>
      <StyledIconButton ref={buttonRef} onClick={onClick}>
        <StyledMoreVertIcon />
      </StyledIconButton>
      <Popper
        open={active}
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper elevation={8}>
            <MenuItemList onUpdate={handleUpdate} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
